/**
 * №16.2.1 — the SHARED contract + derivations for the full process view. Pure TS
 * only (types + functions, no JSX), so both lenses and the rail import from one
 * place and the fast-refresh boundary stays clean. The derivations here are the
 * lane's "adapt, don't fabricate" work: trigger text, opened-date, decision branch
 * tails and the preview/consequence maths — all computed from the projection, none
 * invented (Kuvio has no effect model, so no effect data is ever synthesised).
 */
import type { DetailSegment, DetailStep, DetailDecision, InstanceDetail, WhatsNext, SegmentTrigger, WaitingStep } from "../../hooks/use-workflow";
import type { WorkspaceMember } from "../../hooks/use-workspace-members";
import { assigneeDisplay } from "./wf-assignee";

export type { WaitingStep };

// ─── cross-lens contract ────────────────────────────────────────────────────

/** Which element the rail is describing. Clicking any step/segment/decision sets it. */
export type Selection =
  | { kind: "step"; segmentNo: number; stepEntryNo: number }
  | { kind: "decision"; segmentNo: number }
  | { kind: "segment"; segmentNo: number };

/** A client-only decision preview (drive lens) — NO verb fires until Change. */
export interface Preview { decisionSegmentNo: number; outcome: string; }

export interface Directory { members: WorkspaceMember[]; agentNames: Record<string, string>; currentUserId?: string; }

export interface LensData {
  instanceNo: number;
  detail: InstanceDetail;
  next: WhatsNext;
  directory: Directory;
}

/** The confirmation-free run verbs, pre-bound to this instance (the engine writes). */
export interface WorkflowActions {
  complete(stepEntryNo: number): void;
  recordOutcome(segmentNo: number, outcome: string): void;
  clearOutcome(segmentNo: number): void;
  skip(stepEntryNo: number, reason: string): void;
  setAssignee(stepEntryNo: number, kind: string, code: string | null): void;
  setDue(stepEntryNo: number, date: string | null): void;
  setWorkState(stepEntryNo: number, state: string): void;
  setInstructions(stepEntryNo: number, instructions: string): void;
  bulkAssign(nos: number[], kind: string, code: string | null): void;
  bulkSetDue(nos: number[], date: string | null): void;
  bulkMarkDone(nos: number[]): void;
  bulkSkip(nos: number[], reason: string): void;
}

export interface LensProps {
  data: LensData;
  actions: WorkflowActions;
  busy: boolean;
  selected: Selection | null;
  onSelect: (s: Selection | null) => void;
  preview: Preview | null;
  onPreview: (p: Preview | null) => void;
}

// ─── pure derivations ───────────────────────────────────────────────────────

export function initialsOf(s: string): string {
  const w = s.trim().split(/\s+/).filter(Boolean);
  const base = w.length >= 2 ? w[0]!.charAt(0) + w[1]!.charAt(0) : s.replace(/[^A-Za-z0-9]/g, "").slice(0, 2);
  return (base || "?").toUpperCase();
}

/** Resolve a raw assignee to a display name — person via the member roster, agent
 *  via the directory, role via a friendly label. */
export function resolveAssignee(kind: string, code: string, dir: Directory) {
  if (kind === "person" && code) {
    const m = dir.members.find((mm) => mm.id === code);
    if (m) return { who: m.name || m.email, initials: initialsOf(m.name || m.email), isAgent: false };
  }
  return assigneeDisplay(kind, code, dir.agentNames);
}

export function fmtDate(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}

export function relDays(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const t0 = new Date(); t0.setHours(0, 0, 0, 0);
  const t1 = new Date(d); t1.setHours(0, 0, 0, 0);
  const days = Math.round((t1.getTime() - t0.getTime()) / 86_400_000);
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days === -1) return "yesterday";
  return days > 0 ? `in ${days} days` : `${-days} days ago`;
}

export function isOverdue(iso?: string | null): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const t0 = new Date(); t0.setHours(0, 0, 0, 0);
  const t1 = new Date(d); t1.setHours(0, 0, 0, 0);
  return t1.getTime() < t0.getTime();
}

const STEP_DONE = new Set(["completed", "complete", "done", "skipped"]);
export function stepIsDone(st: DetailStep): boolean { return STEP_DONE.has(st.status); }
export function openSteps(seg: DetailSegment): DetailStep[] { return seg.steps.filter((s) => !stepIsDone(s)); }

/** A field-date type ("settlementDate") → "Settlement date". */
export function prettyDateType(dt: string): string {
  return dt.replace(/[-_]/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^\w/, (c) => c.toUpperCase());
}

/** The segment's "opens when" clause, ADAPTED from its structural trigger. */
export function triggerText(trigger: SegmentTrigger | undefined, segments: DetailSegment[]): string {
  if (!trigger) return "opens on its schedule";
  const nameOf = (no?: number) => segments.find((s) => s.segmentNo === no)?.name ?? `stage ${no ?? "?"}`;
  switch (trigger.kind) {
    case "withMatter": case "on_start": case "start":
      return "the matter opens";
    case "afterDecision": case "decisionReached": {
      const dn = nameOf(trigger.decisionSegmentNo);
      const dec = segments.find((s) => s.segmentNo === trigger.decisionSegmentNo)?.decision;
      const label = dec?.outcomes.find((o) => o.code === trigger.outcome)?.label ?? trigger.outcome ?? "the choice";
      return `“${dn}” is ${label}`;
    }
    case "afterSegments": {
      const deps = (trigger.dependsOn ?? []).map(nameOf);
      const join = trigger.joinMode === "any" ? "any of" : "all of";
      if (deps.length === 0) return "the prior stage finishes";
      if (deps.length === 1) return `“${deps[0]}” finishes`;
      return `${join} ${deps.map((d) => `“${d}”`).join(", ")} finish`;
    }
    case "date":
      return trigger.dateType ? prettyDateType(trigger.dateType) : "a key date";
    case "stepsComplete": case "gateResolves":
      return "this stage’s own work is done";
    default:
      return "its trigger fires";
  }
}

/** The date a segment opened, when derivable from its antecedents' completion. */
export function openedDate(seg: DetailSegment, segments: DetailSegment[]): string | null {
  const t = seg.trigger;
  if (!t) return null;
  if ((t.kind === "afterDecision" || t.kind === "decisionReached") && t.decisionSegmentNo != null) {
    return segments.find((s) => s.segmentNo === t.decisionSegmentNo)?.decision?.decidedAt ?? null;
  }
  if (t.kind === "afterSegments") {
    const dates = (t.dependsOn ?? [])
      .map((no) => segments.find((s) => s.segmentNo === no)?.completedAt)
      .filter((x): x is string => !!x);
    if (dates.length === 0) return null;
    return dates.slice().sort().at(-1) ?? null;
  }
  return null;
}

/** The segments a decision outcome opens (its branch tail). */
export function branchTail(segments: DetailSegment[], decisionSegmentNo: number, outcome: string): DetailSegment[] {
  return segments.filter((s) => s.trigger?.decisionSegmentNo === decisionSegmentNo && s.trigger?.outcome === outcome);
}

/** OCOLOR — a stable, distinct colour per decision OUTCOME, shared between the
 *  option tile and that outcome's downstream branch segments so you can trace
 *  "this option → these steps" by colour. Keyed by the outcome's INDEX within its
 *  decision (stable regardless of which is chosen) into a 5-slot theme-aware
 *  palette (`--wfv-c0`..`--wfv-c4` in workflow-fullview.css). Slot 0 is the Kuvio
 *  accent/green, so the common proceed/default path stays green and the 2-option
 *  case reads green + amber, matching the locked mockup. Returns the CSS var to
 *  set as `--wfv-oc` on the element. */
export function outcomeColorVar(decision: DetailDecision | null | undefined, outcomeCode: string | null | undefined): string {
  const idx = decision?.outcomes.findIndex((o) => o.code === outcomeCode) ?? -1;
  const slot = idx >= 0 ? idx % 5 : 0;
  return `var(--wfv-c${slot})`;
}
